/**
 * V4.24.46 — Loan Detail (Web) — mirrors mobile `loan_detail` screen.
 *
 * Serves:
 *   • Type A (Own Bank) — 4-step: Select → Upload LO → Approve/Reject → SPA Signed
 *   • Type D (Special Handling) — same 4-step, different label
 *   • Type B (Outsource) — read-only summary
 *   • Terminal states (approved / rejected / drawdown / spa_signed) — result view
 *
 * Backend: GET /api/public/v1/loan-cases/:id (single source of truth).
 * Deep-link path: `/loan/:id`.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { FileText, Upload, Check, Loader2, AlertTriangle, CircleCheck, XCircle, ThumbsUp, ThumbsDown, Clock } from 'lucide-react';
import { LoanPageHeader, StatusBadge, routeForNextScreen } from './_shared';

const FOUR_STEP_PATHS = ['A_own_bank', 'D_any_bank'];

function Stepper({ steps }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="loan-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s.step}>
          <div className="flex flex-col items-center min-w-[110px]" data-testid={`loan-step-${s.step}`}>
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
                s.status === 'completed' ? 'bg-emerald-600 text-white'
                  : s.status === 'active' ? 'bg-indigo-600 text-white ring-4 ring-indigo-200'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {s.status === 'completed' ? <Check className="w-4 h-4" /> : s.step}
            </div>
            <p className="text-xs font-medium mt-1 text-center leading-tight">{s.label}</p>
            {s.hint && <p className="text-[10px] text-gray-500 text-center">{s.hint}</p>}
          </div>
          {i < steps.length - 1 && <div className={`flex-1 h-0.5 min-w-[20px] ${s.status === 'completed' ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// V4.24.85 — Offer panel for both Type B (Outsource) and Type C
// (Recommended Bank) — mirrors the mobile "Your loan offers are ready"
// screen (design attachment 1).
//   • Lists every banker on `case.offers[]` (Type B/C parity).
//   • Offers still at `waiting_bank` show a status pill only.
//   • Offers at `approved` are selectable via a radio group. Selecting
//     one and hitting "Review & accept offer →" opens the LO Signing UI
//     (Amount / Rate / Tenure / Monthly / Lock-in / Status = Approved +
//     signature canvas + "Accept & request LO"). On submit we call
//     POST /api/public/v1/loan-cases/:id/offers/:offer_id/accept with
//     `agreement_accepted: true` and the signature. Backend flips the
//     offer to `accepted`, supersedes peers, and advances the case
//     directly to `lo_signed` (V4.24.88).
//   • Offers already `accepted` / `reviewing` render read-only.
function TypeBOfferPanel({ caseData, onAccepted }) {
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const sigRef = useRef(null);
  const drawingRef = useRef(false);
  const [sigEmpty, setSigEmpty] = useState(true);

  // V4.24.88 — Panel renders for Type B AND Type C (mobile parity per
  // user's spec: "Make Type B and Type C follow the same offer → accept
  // → LO Signed behavior"). Show ANY offer on the case (Type B mirror
  // rows AND Type C direct offers).
  const offers = caseData.offers || [];
  if (offers.length === 0) return null;

  const acceptedOffer = offers.find(
    (o) => o.status === 'accepted' || o.id === caseData.accepted_offer_id
  );
  const approvedOffers = offers.filter((o) => o.status === 'approved');
  const hasApprovedOffer = approvedOffers.length > 0;
  const selectedOffer = offers.find((o) => o.id === selectedOfferId);
  const partnerLabel = caseData.outsource_partner_name || caseData.selected_partner_name;

  const openSigningDialog = () => {
    if (!selectedOfferId) return;
    setSigEmpty(true);
    setConfirming(true);
    // Clear the canvas after mount
    setTimeout(() => {
      const c = sigRef.current;
      if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    }, 100);
  };

  const canvasPoint = (e) => {
    const c = sigRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const startDraw = (e) => {
    e.preventDefault(); drawingRef.current = true;
    const ctx = sigRef.current.getContext('2d');
    const { x, y } = canvasPoint(e);
    ctx.beginPath(); ctx.moveTo(x, y);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111827';
  };
  const draw = (e) => {
    if (!drawingRef.current) return; e.preventDefault();
    const ctx = sigRef.current.getContext('2d');
    const { x, y } = canvasPoint(e);
    ctx.lineTo(x, y); ctx.stroke();
    if (sigEmpty) setSigEmpty(false);
  };
  const endDraw = () => { drawingRef.current = false; };
  const clearSig = () => {
    const c = sigRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setSigEmpty(true);
  };

  const doAccept = async () => {
    if (!selectedOfferId) return;
    if (sigEmpty) { toast.error('Please sign to accept'); return; }
    const signature_base64 = sigRef.current.toDataURL('image/png');
    setAccepting(true);
    try {
      const r = await api.post(`/public/v1/loan-cases/${caseData.id}/offers/${selectedOfferId}/accept`, {
        agreement_accepted: true,
        signature_base64,
      });
      toast.success(r.data?.message || 'LO acceptance received');
      setConfirming(false);
      onAccepted?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = (typeof d === 'string') ? d : (d?.message || d?.errors?.[0]?.message || 'Failed to accept offer');
      toast.error(msg);
    } finally { setAccepting(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="type-b-offer-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CircleCheck className="w-5 h-5 text-emerald-600" />
          <div>
            <p className="font-bold text-gray-900">
              {hasApprovedOffer || acceptedOffer ? 'Your loan offers are ready' : 'Bankers assigned'}
            </p>
            <p className="text-xs text-gray-500">
              {offers.length} banker{offers.length > 1 ? 's' : ''}{partnerLabel ? ` assigned via ${partnerLabel}` : ''}
            </p>
          </div>
        </div>
        <StatusBadge status={caseData.status} label={caseData.loan_status_label} />
      </div>

      <div className="space-y-2" data-testid="type-b-offers-list">
        {offers.map((o) => {
          const isSelectable = o.status === 'approved' && !acceptedOffer;
          const isSelected = selectedOfferId === o.id;
          const isAccepted = acceptedOffer?.id === o.id;
          return (
            <label
              key={o.id}
              className={`block border rounded-lg p-3 transition ${
                isAccepted ? 'border-emerald-500 bg-emerald-50'
                : isSelected ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                : isSelectable ? 'cursor-pointer hover:border-emerald-300' : 'opacity-90'
              }`}
              data-testid={`type-b-offer-${o.id}`}
            >
              <div className="flex items-center gap-3">
                {isSelectable && (
                  <input
                    type="radio"
                    name="type-b-offer"
                    checked={isSelected}
                    onChange={() => setSelectedOfferId(o.id)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid={`type-b-offer-radio-${o.id}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-bold ${isAccepted || isSelected ? 'text-emerald-700' : 'text-gray-800'}`}>
                      {o.bank_code || o.bank_name}
                    </p>
                    <OfferStatusPill status={o.status} />
                  </div>
                  {o.banker_name && <p className="text-xs text-gray-500">{o.banker_name}</p>}
                  {o.status === 'approved' && (
                    <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-700">
                      <span className="bg-gray-100 rounded px-2 py-0.5">
                        Loan RM {Number(o.loan_amount || 0).toLocaleString('en-MY')}
                      </span>
                      <span className="bg-gray-100 rounded px-2 py-0.5">{o.tenure_years || '—'} yrs</span>
                      {o.lock_in_years ? <span className="bg-gray-100 rounded px-2 py-0.5">{o.lock_in_years} yr lock-in</span> : null}
                      {o.rate_percent != null && <span className="bg-gray-100 rounded px-2 py-0.5">Rate {o.rate_percent}%</span>}
                    </div>
                  )}
                </div>
                {o.lo_pdf_url && (
                  <a
                    href={o.lo_pdf_url.startsWith('http') ? o.lo_pdf_url : `${process.env.REACT_APP_BACKEND_URL}/api${o.lo_pdf_url}`}
                    target="_blank" rel="noreferrer"
                    className="text-indigo-600 hover:underline text-xs flex items-center gap-1 flex-shrink-0"
                    data-testid={`type-b-offer-lo-${o.id}`}
                  >
                    <FileText className="w-3 h-3" /> LO
                  </a>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {hasApprovedOffer && !acceptedOffer && (
        <Button
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          disabled={!selectedOfferId}
          onClick={openSigningDialog}
          data-testid="type-b-review-accept-btn"
        >
          Review &amp; accept offer →
        </Button>
      )}

      {!hasApprovedOffer && !acceptedOffer && (
        <p className="text-xs text-gray-500 italic">Waiting for the assigned bankers to submit their offers.</p>
      )}

      {/* V4.24.88 — LO Signing UI matches attachment 1.jpeg.
          Renders Amount / Rate / Tenure / Monthly / Lock-in / Status +
          "Next step (offline)" paragraph + signature canvas +
          Accept & request LO button. On submit → case = lo_signed. */}
      <Dialog open={confirming} onOpenChange={(o) => { if (!o) setConfirming(false); }}>
        <DialogContent className="max-w-md" data-testid="type-b-accept-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">Loan</DialogTitle>
          </DialogHeader>
          {selectedOffer && (
            <div className="bg-white rounded-lg border p-4 divide-y" data-testid="lo-signing-offer-summary">
              <SigningRow label="Amount" value={`RM ${Number(selectedOffer.loan_amount || 0).toLocaleString('en-MY')}`} />
              <SigningRow label="Rate" value={selectedOffer.rate_percent != null ? `${selectedOffer.rate_percent}%` : '-'} />
              <SigningRow label="Tenure" value={`${selectedOffer.tenure_years || 0} years`} />
              <SigningRow label="Monthly" value={`RM ${Number(selectedOffer.monthly_installment || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <SigningRow label="Lock-in" value={`${selectedOffer.lock_in_years || 0} years`} />
              <div className="pt-3 flex justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <span className="font-bold text-emerald-700">Approved</span>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg border p-4 space-y-3">
            <p className="font-bold text-gray-900">Next step (offline)</p>
            <p className="text-sm text-gray-700">
              By accepting, {selectedOffer?.bank_code || 'the bank'} will prepare an official Letter of Offer (LO).
              You&apos;ll sign it in person at the bank branch.
            </p>
            <div className="border-2 rounded-lg bg-slate-50">
              <canvas
                ref={sigRef}
                width={520}
                height={160}
                className="w-full touch-none rounded"
                onMouseDown={startDraw} onMouseMove={draw}
                onMouseUp={endDraw} onMouseLeave={endDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                data-testid="lo-signing-canvas"
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {sigEmpty ? 'Sign to accept' : 'Signature captured ✓'}
              </p>
              <button type="button" onClick={clearSig} className="text-red-600 hover:text-red-800 text-sm font-semibold"
                      data-testid="lo-signing-clear">Clear</button>
            </div>
          </div>
          <DialogFooter>
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={accepting || sigEmpty}
              onClick={doAccept}
              data-testid="lo-signing-accept-btn"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Accept &amp; request LO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SigningRow({ label, value }) {
  return (
    <div className="py-2 flex justify-between text-sm first:pt-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-bold text-gray-900">{value}</span>
    </div>
  );
}

const OFFER_STATUS_MAP = {
  // V4.24.85 — `waiting_bank` = banker hasn't submitted yet. Mobile
  // renders this as "Reviewing" (the banker is reviewing the case), so
  // Web mirrors the same wording for parity.
  waiting_bank: { label: 'Reviewing',    cls: 'bg-slate-100 text-slate-700' },
  approved:     { label: 'Approved',     cls: 'bg-emerald-100 text-emerald-700' },
  reviewing:    { label: 'Reviewing',    cls: 'bg-amber-100 text-amber-700' },
  accepted:     { label: 'Accepted',     cls: 'bg-emerald-600 text-white' },
  rejected:     { label: 'Rejected',     cls: 'bg-red-100 text-red-700' },
  superseded:   { label: 'Superseded',   cls: 'bg-slate-100 text-slate-500' },
};

function OfferStatusPill({ status }) {
  const m = OFFER_STATUS_MAP[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${m.cls}`}>{m.label}</span>;
}

// V4.24.91 — Post LO-Signed action panels.
//   • At `lo_signed` → Image 1 rendering:
//       Accepted Offer summary card + "Action needed (after LO sign)"
//       panel with green Mark loan as approved + red Reject Loan.
//   • At `approved`  → Image 2 rendering:
//       "Next: SnP signing →" callout + Commission-claim/Outsource-fee
//       checklist + blue "Schedule SnP signing →" CTA + secondary
//       "Back to bookings" link.
// Uses existing statuses only: lo_signed → approved / rejected via
// POST /api/public/v1/loan-cases/:id/mark-approved and
// POST /api/public/v1/loan-cases/:id/reject-at-lo.
function PostLoSignedPanels({ caseData, onChanged, isStaff, hasSignedLo }) {
  const navigate = useNavigate();
  const [approving, setApproving] = useState(false);
  const [rejectDlg, setRejectDlg] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const offers = caseData.offers || [];
  const acceptedOffer = offers.find(
    (o) => o.status === 'accepted' || o.id === caseData.accepted_offer_id
  );

  const doApprove = async () => {
    if (!hasSignedLo) { toast.error('Upload the signed LO document first'); return; }
    setApproving(true);
    try {
      await api.post(`/public/v1/loan-cases/${caseData.id}/mark-approved`);
      toast.success('Loan marked as approved');
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || 'Failed to mark approved');
    } finally { setApproving(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setRejecting(true);
    try {
      await api.post(`/public/v1/loan-cases/${caseData.id}/reject-at-lo`, { reason: rejectReason });
      toast.success('Loan rejected');
      setRejectDlg(false);
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || 'Failed to reject');
    } finally { setRejecting(false); }
  };

  // Image 1 — at lo_signed
  if (caseData.status === 'lo_signed' && acceptedOffer) {
    const bank = acceptedOffer.bank_code || acceptedOffer.bank_name;
    const banker = acceptedOffer.banker_name;
    return (
      <>
        <Card className="p-5 bg-emerald-50 border-emerald-200" data-testid="accepted-offer-card">
          <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Accepted Offer</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{bank}{banker ? ` · ${banker}` : ''}</p>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-xs text-gray-500">Amount</p>
              <p className="font-bold text-gray-900">RM {Number(acceptedOffer.loan_amount || 0).toLocaleString('en-MY')}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tenure</p>
              <p className="font-bold text-gray-900">{acceptedOffer.tenure_years || 0} yrs</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Rate</p>
              <p className="font-bold text-gray-900">{acceptedOffer.rate_percent != null ? `${acceptedOffer.rate_percent}%` : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Monthly</p>
              <p className="font-bold text-gray-900">{acceptedOffer.monthly_installment ? `RM ${Number(acceptedOffer.monthly_installment).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-amber-50 border-amber-200 space-y-3" data-testid="action-after-lo-sign-panel">
          <p className="font-bold text-amber-800 text-lg">Action needed (after LO sign)</p>
          {isStaff ? (
            <>
              <p className="text-sm text-gray-800">Once customer signs LO at branch:</p>
              <ol className="text-sm text-gray-800 space-y-1 list-decimal pl-5">
                <li>Confirm with customer</li>
                <li>Upload the scanned/photo of the signed LO (below)</li>
                <li>Tap below to confirm</li>
              </ol>
              {!hasSignedLo && (
                <p className="text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded p-2 flex items-start gap-1" data-testid="lo-required-hint">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Upload the signed LO document first — the loan cannot be marked approved until it is uploaded.
                </p>
              )}
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 font-bold disabled:opacity-50"
                disabled={approving || !hasSignedLo}
                onClick={doApprove}
                data-testid="mark-loan-approved-btn"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Mark loan as approved
              </Button>
              <Button
                className="w-full bg-red-600 hover:bg-red-700 font-bold"
                disabled={rejecting}
                onClick={() => setRejectDlg(true)}
                data-testid="reject-loan-btn"
              >
                Reject Loan
              </Button>
            </>
          ) : (
            <div className="text-sm text-gray-800 space-y-2" data-testid="customer-waiting-approval">
              <p>
                {hasSignedLo
                  ? 'Your signed Letter of Offer has been uploaded.'
                  : 'Please upload your signed Letter of Offer (LO) above once you have signed it at the branch.'}
              </p>
              <p className="flex items-center gap-2 text-amber-800 font-medium">
                <Clock className="w-4 h-4" />
                Waiting for your agent to confirm approval.
              </p>
            </div>
          )}
        </Card>

        <Dialog open={rejectDlg} onOpenChange={(o) => { if (!o) setRejectDlg(false); }}>
          <DialogContent data-testid="reject-loan-dialog">
            <DialogHeader>
              <DialogTitle>Reject Loan</DialogTitle>
              <DialogDescription>Please provide a reason. This will be recorded in the audit trail.</DialogDescription>
            </DialogHeader>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection"
              className="w-full border rounded-lg p-3 min-h-[100px] text-sm"
              data-testid="reject-loan-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDlg(false)} disabled={rejecting}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" disabled={rejecting || !rejectReason.trim()} onClick={doReject} data-testid="reject-loan-confirm">
                {rejecting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Confirm reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Image 2 — at approved
  if (caseData.status === 'approved') {
    return (
      <>
        <Card className="p-5 border-2 border-blue-300 bg-blue-50" data-testid="next-snp-signing-panel">
          <p className="font-bold text-blue-700 text-lg">Next: SnP signing →</p>
          <p className="text-sm text-gray-800 mt-2">Schedule Sale &amp; Purchase Agreement signing with seller&apos;s solicitor.</p>
          <p className="text-xs text-gray-500 mt-2">Within 14 days per booking T&amp;C.</p>
        </Card>

        <Card className="p-4 divide-y" data-testid="approved-checklist">
          <div className="py-3 flex items-center gap-2 text-emerald-700 font-semibold first:pt-0">
            <CircleCheck className="w-5 h-5" />
            <span>Commission claim auto-drafted</span>
          </div>
          <div className="py-3 flex items-center gap-2 text-emerald-700 font-semibold">
            <CircleCheck className="w-5 h-5" />
            <span>Outsource fee (if any) credited</span>
          </div>
        </Card>

        <Button
          className="w-full bg-blue-600 hover:bg-blue-700 font-bold"
          onClick={() => navigate(isStaff ? '/bookings' : '/my-bookings')}
          data-testid="schedule-snp-signing-btn"
        >
          Schedule SnP signing →
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(isStaff ? '/bookings' : '/my-bookings')}
          data-testid="back-to-bookings-btn"
        >
          Back to bookings
        </Button>
      </>
    );
  }

  return null;
}

export default function LoanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // V4.24.47 — Agent Approve / Reject decision state.
  const [decision, setDecision] = useState(null); // 'approve' | 'reject' | null
  const [deciding, setDeciding] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/public/v1/loan-cases/${id}`);
      setCaseData(r.data?.data || null);
      const p = await api.get(`/public/v1/loan-cases/${id}/progress`).catch(() => null);
      if (p) setProgress(p.data?.data || null);
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed to load case'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // V4.24.46 — Auto-redirect to the correct screen if the user landed here
  // but the case belongs elsewhere (e.g. Type C at ready_pick_banker →
  // pick-bank screen). Uses the same next_screen contract as mobile.
  //
  // V4.24.77 — Pass `selection_path` so `routeForNextScreen` correctly
  // distinguishes between the type-picker screen (`/score`, when no path
  // is set) and the Type-C stage-2 fan-out (`/pick-bank`). Without this,
  // any case reaching `ready_pick_banker` was force-redirected to
  // `/pick-bank`, skipping the CCRIS + Choose-selection-path wizard.
  useEffect(() => {
    if (!caseData) return;
    const target = routeForNextScreen(id, caseData.next_screen, caseData.selection_path);
    // Only redirect if we're clearly NOT the intended screen. Type A/D +
    // terminal states legitimately live at /loan/:id.
    const isFourStep = FOUR_STEP_PATHS.includes(caseData.selection_path);
    const isTerminal = ['approved', 'rejected', 'drawdown', 'closed', 'spa_signed'].includes(caseData.status);
    const isTypeB = caseData.selection_path === 'B_outsource';
    // V4.24.90 — At `offer_accepted` / `lo_signed` / `lo_meetup` the
    // customer arrives here from `/loan-offers` to Review & accept the
    // approved offer (see TypeBOfferPanel signing dialog). The backend's
    // `next_screen` still hints `loan_offers`, so without this guard the
    // auto-redirect bounces the user right back to the list — making
    // the offer card look "dead". Keep the user on the detail page for
    // these post-banker states so they can proceed to LO signing.
    const isOfferPhase = ['offer_accepted', 'lo_meetup', 'lo_signed'].includes(caseData.status);
    // V4.25.0 — Type B at `bankers_available` must go to the "Available
    // Banker" selection screen (agent picks assigned bankers). Allow the
    // redirect even though it's a Type B case.
    const isAvailableBankers = caseData.status === 'bankers_available';
    if (!isFourStep && !isTerminal && (!isTypeB || isAvailableBankers) && !isOfferPhase && target !== `/loan/${id}`) {
      navigate(target, { replace: true });
    }
  }, [caseData, id, navigate]);

  const handleLoUpload = async (file) => {
    if (!file || file.type !== 'application/pdf') { toast.error('PDF only'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const up = await api.post(`/loans/cases/${id}/signed-lo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Signed LO uploaded');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  // V4.24.47 — Agent-only Approve / Reject. Hits the same mobile agent
  // status endpoint so behaviour (LO gate + booking sync + refund
  // auto-create on rejection) is identical to the mobile app.
  const submitDecision = async () => {
    if (!decision) return;
    setDeciding(true);
    try {
      const target = decision === 'approve' ? 'approved' : 'rejected';
      await api.put(`/public/v1/loan-cases/${id}/status`, {
        status: target,
        notes: decision === 'approve' ? 'Agent approved' : 'Agent rejected',
      });
      toast.success(decision === 'approve' ? 'Loan approved' : 'Loan rejected — refund initiated');
      setDecision(null);
      navigate('/loan-offers');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Action failed');
    } finally { setDeciding(false); }
  };

  if (loading || !caseData) {
    return <div className="p-6 text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  const path = caseData.selection_path || '';
  const isFourStep = FOUR_STEP_PATHS.includes(path);
  const isTypeB = path === 'B_outsource';
  const isTerminal = ['approved', 'rejected', 'closed', 'drawdown', 'spa_signed'].includes(caseData.status);
  const signedLoFiles = caseData.signed_lo_files || [];
  const hasSignedLo = signedLoFiles.length > 0;
  // V4.25.13 — Only staff (agent/admin/PIC/account) may approve/reject; the
  // home buyer can upload the signed LO + view. Also surface the signed-LO
  // upload card for Type B/C during the post-acceptance stages (previously
  // only Type A/D had it).
  const isStaff = !!(user && user.role && user.role !== 'customer');
  const isPostAcceptPhase = ['offer_accepted', 'lo_meetup', 'lo_signed'].includes(caseData.status);
  const showLoUploadCard = isFourStep
    || ((path === 'B_outsource' || path === 'C_recommended') && isPostAcceptPhase);
  const acceptedOffer = (caseData.offers || []).find(o => o.id === caseData.accepted_offer_id) ||
                        (caseData.offers || []).find(o => o.status === 'approved');

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-detail-page">
      <LoanPageHeader caseData={caseData} title={isFourStep ? `Loan Type ${path === 'A_own_bank' ? 'A' : 'D'}` : 'Loan Detail'} />

      {progress?.steps?.length > 0 && <Stepper steps={progress.steps} />}

      {/* V4.24.50 — Type B (Outsource) dedicated card.
          V4.24.85 — Now also renders the list of bankers assigned by the
          Outsource Portal + the Review-&-accept-offer wizard once at
          least one banker has submitted an approved offer (image 2 & 3
          from the design). Data source: `caseData.offers[]` mirror
          entries created by `_sync_customer_offer` — same source used by
          Mobile so no data duplication. */}
      {isTypeB && (
        <>
          <Card className="p-4 space-y-3" data-testid="type-b-outsource-card">
            <p className="text-xs uppercase tracking-wider text-gray-500">Loan Type B — Outsource</p>
            {caseData.outsource_partner_name ? (
              <>
                <p className="font-bold text-gray-800 text-lg">{caseData.outsource_partner_name}</p>
                <p className="text-sm text-gray-600">
                  This agency is handling your loan application. They will contact you directly to complete paperwork
                  and submit to the bank on your behalf. You&apos;ll be notified when the case moves forward.
                </p>
                {caseData.outsource_partner_contact && (
                  <p className="text-xs text-gray-500">Contact: {caseData.outsource_partner_contact}</p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  No outsource partner assigned yet.
                </p>
                <Button
                  onClick={() => navigate(`/loan/${id}/pick-outsource`)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="type-b-pick-partner-btn"
                >
                  Pick a Partner
                </Button>
              </>
            )}
          </Card>

          <TypeBOfferPanel caseData={caseData} onAccepted={load} />
        </>
      )}

      {/* V4.24.88 — Type C (Recommended Bank) parity: render the same
          offer / Review-&-accept / LO signing wizard for Type C so both
          types follow the identical offer → accept → lo_signed flow. */}
      {path === 'C_recommended' && (caseData.offers || []).length > 0 && (
        <TypeBOfferPanel caseData={caseData} onAccepted={load} />
      )}

      {/* V4.24.91 — Post LO-Signed panels (Image 1 & 2).
          Applies to both Type B (outsource) and Type C (recommended). */}
      {(path === 'B_outsource' || path === 'C_recommended') && (
        <PostLoSignedPanels caseData={caseData} onChanged={load} isStaff={isStaff} hasSignedLo={hasSignedLo} />
      )}

      {/* Signed LO upload card — Type A/D always; Type B/C during post-accept stages (V4.25.13) */}
      {showLoUploadCard && (
        <Card className="p-4 space-y-3" data-testid="signed-lo-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Signed Letter of Offer (PDF)
                {hasSignedLo && <Badge className="bg-emerald-100 text-emerald-700">Uploaded</Badge>}
              </p>
              <p className="text-xs text-gray-500">
                {isFourStep
                  ? `Required before Approve / Reject for ${path === 'A_own_bank' ? 'Type A (Own Bank)' : 'Type D (Special Handling)'}.`
                  : 'Required before the loan can be marked approved.'}
              </p>
            </div>
            {!isTerminal && (
              <>
                <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => handleLoUpload(e.target.files?.[0])} data-testid="signed-lo-file-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="signed-lo-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                  {hasSignedLo ? 'Replace' : 'Upload'}
                </Button>
              </>
            )}
          </div>
          {hasSignedLo && (
            <ul className="text-sm space-y-1 mt-2" data-testid="signed-lo-list">
              {signedLoFiles.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <FileText className="w-3 h-3 text-gray-400" />
                  <a href={f.url?.startsWith('http') ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                    {f.name || 'Signed LO.pdf'}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {!hasSignedLo && !isTerminal && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              Upload the signed LO document first — {isFourStep ? 'Approve/Reject decisions are locked' : 'the loan cannot be marked approved'} until the file is uploaded.
            </p>
          )}
        </Card>
      )}

      {/* V4.24.47 — Agent-only Approve / Reject card for Type A / D 4-step flow. */}
      {isFourStep && !isTerminal && user?.role === 'agent' && (
        <Card className="p-4 space-y-3" data-testid="agent-decision-card">
          <p className="font-semibold text-gray-800">Agent Decision</p>
          <p className="text-xs text-gray-500">
            As the assigned agent, you can approve or reject this loan on behalf of the customer once the signed LO has been uploaded.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setDecision('approve')}
              disabled={!hasSignedLo || deciding}
              data-testid="agent-approve-btn"
            >
              <ThumbsUp className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDecision('reject')}
              disabled={!hasSignedLo || deciding}
              data-testid="agent-reject-btn"
            >
              <ThumbsDown className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
        </Card>
      )}

      {/* Accepted / active offer summary */}
      {acceptedOffer && (
        <Card className="p-4 space-y-2" data-testid="accepted-offer-card">
          <p className="text-xs uppercase tracking-wider text-gray-500">Approved Bank Offer</p>
          <p className="font-semibold text-gray-800">{acceptedOffer.bank_code} — {acceptedOffer.rate_percent}%</p>
          <p className="text-sm text-gray-600">RM {Number(acceptedOffer.loan_amount).toLocaleString('en-MY')} · {acceptedOffer.tenure_years} years</p>
          {acceptedOffer.banker_name && <p className="text-xs text-gray-500">Banker: {acceptedOffer.banker_name}</p>}
        </Card>
      )}

      {/* Terminal state banner */}
      {isTerminal && (
        <Card className={`p-4 flex items-start gap-3 ${caseData.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="terminal-banner">
          {caseData.status === 'rejected' ? <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" /> : <CircleCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />}
          <div>
            <p className="font-semibold">Loan {caseData.loan_status_label || caseData.status}</p>
            <p className="text-sm text-gray-600 mt-0.5">
              {caseData.status === 'rejected' ? 'A refund has been initiated automatically. Check the Refunds page for status.' : 'No further action needed on this case.'}
            </p>
          </div>
        </Card>
      )}

      {/* V4.24.47 — Approve / Reject confirmation modal. Terminal, no reason. */}
      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) setDecision(null); }}>
        <DialogContent data-testid="agent-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision === 'approve' ? 'Approve this loan?' : 'Reject this loan?'}
            </DialogTitle>
            <DialogDescription>
              {decision === 'approve'
                ? 'This will mark the loan as Approved. The booking status will move to loan_approved. This is final and cannot be undone.'
                : 'This will mark the loan as Rejected. A refund will be automatically initiated and shown in Refund Management. This is final and cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} disabled={deciding} data-testid="agent-decision-cancel">
              Cancel
            </Button>
            <Button
              className={decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              variant={decision === 'reject' ? 'destructive' : 'default'}
              onClick={submitDecision}
              disabled={deciding}
              data-testid="agent-decision-confirm"
            >
              {deciding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {decision === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
